import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import Snackbars from '../../component/Snackbars'
import API from '../../component/Axios'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { LIST_FILTER_DATE_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { ADMIN_ACCESS_MODES } from '../../utils/sessionUtils'
import { formatSafeNumber as formatNumber } from '../../utils/numberDisplay'

const createEmptyCountrySignupAnalytics = () => ({
    countries: [],
    monthly: [],
    totals: [],
    rows: [],
    range: null,
    overseas: null,
})

const COUNTRY_CHART_COLORS = {
    MX: '#3182f6',
    BR: '#00a832',
    US: '#ff8a00',
    JP: '#f04452',
    CA: '#8b5cf6',
    GB: '#0891b2',
    ES: '#d97706',
    PT: '#db2777',
}

// 위에 없는 국가에 돌려쓰는 색. 한 차트에 4개국만 그리므로 4색이면 겹치지 않는다.
const COUNTRY_CHART_FALLBACK_COLORS = ['#3182f6', '#00a832', '#ff8a00', '#f04452']

const getCountryChartColor = (countryId, indexInGroup) => (
    COUNTRY_CHART_COLORS[countryId]
    || COUNTRY_CHART_FALLBACK_COLORS[indexInGroup % COUNTRY_CHART_FALLBACK_COLORS.length]
)

// 한 차트에 담을 국가 수. 더 넣으면 선이 겹쳐 읽기 어렵다.
const COUNTRY_CHART_GROUP_SIZE = 4

// 선택 국가를 4개씩 끊어 차트 묶음을 만든다.
const buildCountryChartGroups = (countries) => {
    const groups = []

    for (let index = 0; index < countries.length; index += COUNTRY_CHART_GROUP_SIZE) {
        const groupCountries = countries.slice(index, index + COUNTRY_CHART_GROUP_SIZE)

        groups.push({
            key: groupCountries.map(country => country.countryId).join('-'),
            title: groupCountries.map(country => country.name || country.countryId).join(' · '),
            countries: groupCountries,
        })
    }

    return groups
}

const PERIOD_OPTIONS = [
    { value: 'day', label: '일별' },
    { value: 'month', label: '월별' },
]

const getThreeMonthsAgoDate = (date) => {
    const targetYear = date.getFullYear()
    const targetMonth = date.getMonth() - 3
    const lastDateOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate()

    return new Date(targetYear, targetMonth, Math.min(date.getDate(), lastDateOfTargetMonth))
}

const formatDateParam = (value) => {
    if (!value) {
        return ''
    }

    const date = value instanceof Date ? value : new Date(value)

    if (Number.isNaN(date.getTime())) {
        return ''
    }

    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-')
}

function CountrySignupChart({ chartData, countries, rangePeriod, title, totalsMap }) {
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                {countries.map((country, countryIndex) => (
                    <Box
                        key={country.countryId}
                        sx={{
                            minWidth: 132,
                            py: 0.75,
                            pr: 2,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">{country.name} 가입수</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: getCountryChartColor(country.countryId, countryIndex) }}>
                            {formatNumber(totalsMap[country.countryId]?.signupCount)}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ width: '100%', height: 420 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 12, right: 24, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={value => formatNumber(value)} tick={{ fontSize: 12 }} width={64} />
                        <Tooltip formatter={(value, name) => [formatNumber(value), name]} labelFormatter={label => `${rangePeriod === 'month' ? '월' : '일자'} ${label}`} />
                        <Legend />
                        {countries.map((country, countryIndex) => (
                            <Line
                                key={country.countryId}
                                type="monotone"
                                dataKey={country.countryId}
                                name={country.name}
                                stroke={getCountryChartColor(country.countryId, countryIndex)}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    )
}

// 해외(한국 제외) 전체 가입자 표. 위 국가 선택과 무관하게 모든 해외 국가 합계다.
function OverseasSignupTable({ overseas, rangePeriod }) {
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>해외 가입자 (한국 제외)</Typography>
                <Typography variant="body2" color="text.secondary">
                    기간 합계 <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatNumber(overseas.signupCount)}</Box>명
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>{rangePeriod === 'month' ? '월' : '일자'}</TableCell>
                            <TableCell align="right">가입자 수</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(overseas.rows || []).map(row => (
                            <TableRow key={row.dateKey} hover>
                                <TableCell>{row.date}</TableCell>
                                <TableCell align="right">{formatNumber(row.signupCount)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default function AppCountrySignups({ adminId, role, accessMode, countries, countryFilterSlot }) {
    const history = useHistory()
    const initialEndDateRef = useRef(new Date())
    const initialStartDateRef = useRef(getThreeMonthsAgoDate(initialEndDateRef.current))
    const requestSequenceRef = useRef(0)
    const [loading, setLoading] = useState(true)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('success')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [countrySignupAnalytics, setCountrySignupAnalytics] = useState(createEmptyCountrySignupAnalytics)
    const [period, setPeriod] = useState('day')
    const [startDate, setStartDate] = useState(initialStartDateRef.current)
    const [endDate, setEndDate] = useState(initialEndDateRef.current)
    // 국가를 바꿔 다시 조회할 때 운영자가 보던 기간·단위를 유지한다.
    const filtersRef = useRef({
        startDate: initialStartDateRef.current,
        endDate: initialEndDateRef.current,
        period: 'day',
    })

    useEffect(() => {
        filtersRef.current = { startDate, endDate, period }
    }, [endDate, period, startDate])

    const totalsMap = useMemo(() => {
        return (countrySignupAnalytics.totals || countrySignupAnalytics.monthly || []).reduce((acc, item) => {
            acc[item.countryId] = item
            return acc
        }, {})
    }, [countrySignupAnalytics.monthly, countrySignupAnalytics.totals])

    const rangePeriod = countrySignupAnalytics.range?.period || period

    const chartData = useMemo(() => {
        return (countrySignupAnalytics.rows || [])
            .slice()
            .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
            .map(row => {
                const item = {
                    date: rangePeriod === 'month'
                        ? `${row.dateKey.substring(0, 4)}-${row.dateKey.substring(4, 6)}`
                        : `${row.dateKey.substring(4, 6)}-${row.dateKey.substring(6, 8)}`,
                }

                ;(countrySignupAnalytics.countries || []).forEach(country => {
                    item[country.countryId] = Number(row.countries?.[country.countryId]?.signupCount) || 0
                })

                return item
            })
    }, [countrySignupAnalytics.countries, countrySignupAnalytics.rows, rangePeriod])

    const chartGroups = useMemo(
        () => buildCountryChartGroups(countrySignupAnalytics.countries || []),
        [countrySignupAnalytics.countries],
    )

    const selectedRangeText = useMemo(() => {
        const range = countrySignupAnalytics.range

        if (!range?.startDate || !range?.endDate) {
            return ''
        }

        const periodLabel = range.period === 'month' ? '월별' : '일별'

        return `${periodLabel} | ${range.startDate} ~ ${range.endDate}`
    }, [countrySignupAnalytics.range])

    const getData = useCallback(async (startDateValue, endDateValue, periodValue) => {
        const requestStartDate = formatDateParam(startDateValue)
        const requestEndDate = formatDateParam(endDateValue)

        if (!requestStartDate || !requestEndDate) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('시작 날짜와 종료 날짜를 선택하세요.')
            return
        }

        if (requestStartDate > requestEndDate) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('시작 날짜는 종료 날짜보다 늦을 수 없습니다.')
            return
        }

        const requestId = requestSequenceRef.current + 1
        requestSequenceRef.current = requestId
        setLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/dashboard/getCountrySignupList', {
                adminId,
                role,
                period: periodValue,
                startDate: requestStartDate,
                endDate: requestEndDate,
                countries,
            })

            if (requestId !== requestSequenceRef.current) {
                return
            }

            if (data.code === 200) {
                setCountrySignupAnalytics(data.data || createEmptyCountrySignupAnalytics())
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            if (requestId !== requestSequenceRef.current) {
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(err.response?.data?.message || err.message || '국가별 회원가입수 조회 중 오류가 발생했습니다.')
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false)
            }
        }
    }, [adminId, countries, history, role])

    // getData 가 countries 를 의존하므로 국가를 바꾸면 여기서 다시 조회된다.
    useEffect(() => {
        if (adminId && (role > 3 || accessMode === ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY)) {
            getData(filtersRef.current.startDate, filtersRef.current.endDate, filtersRef.current.period)
        }
    }, [accessMode, adminId, getData, role])

    const handleSubmit = (e) => {
        e.preventDefault()
        getData(startDate, endDate, period)
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handlePeriodChange = (event, nextPeriod) => {
        if (nextPeriod) {
            setPeriod(nextPeriod)
            getData(startDate, endDate, nextPeriod)
        }
    }

    const handleStartDateChange = (nextStartDate) => {
        setStartDate(nextStartDate)

        if (nextStartDate) {
            getData(nextStartDate, endDate, period)
        }
    }

    const handleEndDateChange = (nextEndDate) => {
        setEndDate(nextEndDate)

        if (nextEndDate) {
            getData(startDate, nextEndDate, period)
        }
    }

    if (role <= 3 && accessMode !== ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY) {
        return null
    }

    return (
        <>
            <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Box>
                            <Typography variant="h6">국가별 회원가입수</Typography>
                            {selectedRangeText && (
                                <Typography variant="body2" color="text.secondary">
                                    조회 기간: {selectedRangeText}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={period}
                                onChange={handlePeriodChange}
                                aria-label="signup period"
                                sx={{
                                    height: 36,
                                    '& .MuiToggleButton-root': {
                                        px: 1.25,
                                    },
                                }}
                            >
                                {PERIOD_OPTIONS.map(option => (
                                    <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                                        {option.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            {countryFilterSlot}
                            <ListFilterBar sx={{ width: 'auto', justifyContent: 'flex-end', mt: 0, mb: 0 }}>
                                <ListFilterForm onSubmit={handleSubmit}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            minDate={new Date(2021, 0, 1)}
                                            maxDate={endDate || new Date()}
                                            value={startDate}
                                            onChange={handleStartDateChange}
                                            renderInput={(params) => <TextField required {...params} label="시작 날짜" size="small" sx={LIST_FILTER_DATE_FIELD_SX} />}
                                        />
                                        <DesktopDatePicker
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            minDate={startDate || new Date(2021, 0, 1)}
                                            maxDate={new Date()}
                                            value={endDate}
                                            onChange={handleEndDateChange}
                                            renderInput={(params) => <TextField required {...params} label="종료 날짜" size="small" sx={LIST_FILTER_DATE_FIELD_SX} />}
                                        />
                                    </LocalizationProvider>
                                </ListFilterForm>
                            </ListFilterBar>
                        </Box>
                    </Box>
                    {loading ? (
                        <Spiner/>
                    ) : (countrySignupAnalytics.countries || []).length === 0 ? (
                        <ListEmptyState
                            title="조회된 회원가입 데이터가 없습니다."
                            description="조회 기간을 변경한 뒤 다시 확인해보세요."
                        />
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {countrySignupAnalytics.overseas && (
                                <OverseasSignupTable
                                    overseas={countrySignupAnalytics.overseas}
                                    rangePeriod={rangePeriod}
                                />
                            )}
                            {chartGroups.map(group => (
                                <CountrySignupChart
                                    key={group.key}
                                    title={group.title}
                                    countries={group.countries}
                                    totalsMap={totalsMap}
                                    chartData={chartData}
                                    rangePeriod={rangePeriod}
                                />
                            ))}
                        </Box>
                    )}
                </Paper>
            </Grid>
            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
        </>
    )
}
